import { useEffect, useCallback, useRef } from 'react';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useNotifications } from '../contexts/NotificationContext';
import { useAlerts } from '../contexts/AlertContext';
import {
    ALERT_EVENTS,
    AlertEventName,
    WebSocketAlert,
    AlertPriority,
    getAlertNavigationUrl,
    getAlertTitle,
    getAlertMessage,
    EVENT_CATEGORY_MAP,
} from '../lib/alertTypes';

/**
 * Determines if an alert should be shown as an overlay based on priority
 */
const shouldShowOverlay = (priority: AlertPriority): boolean => {
    return priority === 'critical' || priority === 'high';
};

/**
 * Hook to listen for new order notifications via WebSocket
 * Automatically subscribes when the component mounts
 */
export const useOrderNotifications = () => {
    const { subscribe, isConnected } = useWebSocket();

    useEffect(() => {
        if (!isConnected) return;

        // Subscribe to new_order events - handled by main hook now
        const unsubscribe = subscribe('new_order', (data) => {
            console.log('New order received:', data);
        });

        // Subscribe to order_status_update events
        const unsubscribeStatus = subscribe('order_status_change', (data) => {
            console.log('Order status updated:', data);
        });

        return () => {
            unsubscribe();
            unsubscribeStatus();
        };
    }, [isConnected, subscribe]);

    return { isConnected };
};

/**
 * Comprehensive hook to listen for all WebSocket notifications
 * Routes alerts by priority:
 * - critical/high → Alert overlay (popup)
 * - normal/low → Notification center only
 */
export const useWebSocketNotifications = () => {
    const { subscribe, isConnected, emit, socket } = useWebSocket();
    const { addNotification } = useNotifications();
    const { showAlert } = useAlerts();
    const hasJoinedRoom = useRef(false);

    // Join admin room on connection
    useEffect(() => {
        if (!isConnected || !socket) return;

        // Only join once per connection
        if (!hasJoinedRoom.current) {
            console.log('Joining admin room...');
            emit('join_room', { userType: 'admin' });
            hasJoinedRoom.current = true;
        }

        // Listen for room join confirmation
        const handleRoomJoined = (data: { room: string; success: boolean }) => {
            console.log('Room joined:', data.room, data.success);
        };

        socket.on('room_joined', handleRoomJoined);

        return () => {
            socket.off('room_joined', handleRoomJoined);
        };
    }, [isConnected, socket, emit]);

    // Reset room join status on disconnect
    useEffect(() => {
        if (!isConnected) {
            hasJoinedRoom.current = false;
        }
    }, [isConnected]);

    // Handle all alert events
    useEffect(() => {
        if (!isConnected) return;

        const unsubscribers: (() => void)[] = [];

        // Helper to process alerts
        const handleAlert = (eventType: AlertEventName, data: WebSocketAlert) => {
            const priority = data._meta?.priority || 'normal';
            const title = getAlertTitle(eventType, data);
            const message = getAlertMessage(eventType, data);
            const navigationUrl = getAlertNavigationUrl(eventType, data);
            const category = EVENT_CATEGORY_MAP[eventType];

            console.log(`[${eventType}] Priority: ${priority}`, data);

            // Route based on priority
            if (shouldShowOverlay(priority)) {
                // Show as overlay popup for critical/high priority
                showAlert({
                    eventType,
                    title,
                    message,
                    priority,
                    action: navigationUrl ? { label: 'View', url: navigationUrl } : undefined,
                    rawData: data,
                });
            }

            // Always add to notification center (for history)
            addNotification({
                title,
                message,
                type: priority === 'critical' || priority === 'high' ? 'warning' : 'info',
                priority,
                category,
                action: navigationUrl ? { label: 'View', url: navigationUrl } : undefined,
                eventType,
            });
        };

        // Subscribe to all alert events
        ALERT_EVENTS.forEach((eventType) => {
            unsubscribers.push(
                subscribe(eventType, (data: WebSocketAlert) => {
                    handleAlert(eventType, data);
                })
            );
        });

        return () => {
            unsubscribers.forEach(unsub => unsub());
        };
    }, [isConnected, subscribe, addNotification, showAlert]);

    return { isConnected };
};

/**
 * Hook for specific event subscriptions
 * Use when you only need to listen to specific events in a component
 */
export const useAlertSubscription = (
    eventTypes: AlertEventName[],
    callback: (eventType: AlertEventName, data: WebSocketAlert) => void
) => {
    const { subscribe, isConnected } = useWebSocket();

    useEffect(() => {
        if (!isConnected) return;

        const unsubscribers = eventTypes.map((eventType) =>
            subscribe(eventType, (data: WebSocketAlert) => {
                callback(eventType, data);
            })
        );

        return () => {
            unsubscribers.forEach(unsub => unsub());
        };
    }, [isConnected, subscribe, eventTypes, callback]);

    return { isConnected };
};
